import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterMatchMode, LazyLoadEvent, MessageService } from 'primeng/api';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { Table, TableModule, TableLazyLoadEvent } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import {
  DATE_FORMAT,
  LAST_UPDATED_PROPERTY,
  SCHEDULED_DATE_PROPERTY,
} from './constants/xpass-variables.constant';
import { IsModifiedOptionLabels } from './enums/is-modified-option-labels.enum';
import { XpassLocationLabels } from './enums/xpass-location-labels.enum';
import { XpassProductLabels } from './enums/xpass-product-labels.enum';
import { XpassStatusLabels } from './enums/xpass-status-labels.enum';
import { XpassOrder } from './interfaces/xpass-order.interface';
import { XpassOrdersTableRow } from './interfaces/xpass-orders-table-row.interface';
import { GetTransferOrdersRequest } from './interfaces/get-transfer-orders-request.interface';
import { CommonTypeService } from './services/common-type.service';
import { OrderService } from './services/order.service';
import { StatusPillStylingPipe } from './status-pill-styling/status-pill-styling.pipe';
import { XpassBookingDetailComponent } from '../xpass-detail/xpass-booking-detail';
import { CommonModule } from '@angular/common';
import { Utils } from 'app/utils';
import moment from 'moment';
import { DatePipe } from '@angular/common';
import { NgIf } from '@angular/common';
import { OptionInterface } from '../../../models/option.interface';
import { IpassValidOptionLabels } from './enums/ipass-valid-option-labels.enum';
import { ExportFileTypeModel } from 'app/models/export-file-type.model';
import { ExportFileTypeEnum, ExportFileExtension } from 'app/models';
import { XpassTripTypeLabels } from './enums/xpass-trip-type-labels.enum';
import { TransferTripType } from './enums/transfer-trip-type.enum';

@Component({
  selector: 'xpass',
  standalone: true,
  imports: [
    TableModule,
    FormsModule,
    TagModule,
    DropdownModule,
    CalendarModule,
    StatusPillStylingPipe,
    CommonModule,
    XpassBookingDetailComponent,
    DatePipe,
    DialogModule,
    ToastModule,
    NgIf,
    SidebarModule,
    ButtonModule,
    ProgressSpinnerModule
  ],
  providers: [MessageService],
  templateUrl: './xpass.component.html',
  styleUrl: './xpass.component.scss',
})
export class XpassComponent implements OnInit {
  locationLabels = Object.values(XpassLocationLabels);
  productLabels = Object.values(XpassProductLabels);
  statusLabels = Object.values(XpassStatusLabels);
  isModifiedOptionsLabel = Object.values(IsModifiedOptionLabels);
  isIpassValidOptionsLabel = Object.values(IpassValidOptionLabels);
  tripTypeLabels = Object.values(XpassTripTypeLabels).filter(
    (label) => label !== XpassTripTypeLabels.NONE
  );

  SCHEDULED_DATE_PROPERTY = SCHEDULED_DATE_PROPERTY;
  LAST_UPDATED_PROPERTY = LAST_UPDATED_PROPERTY;

  orders: XpassOrdersTableRow[] = [];
  totalRecords: number = 0;
  loading: boolean = true;
  rows: number = 10;

  locationOptions: OptionInterface[] = [
    { label: XpassLocationLabels.BORACAY, value: XpassLocationLabels.BORACAY, disabled: false },
    { label: XpassLocationLabels.EL_NIDO, value: XpassLocationLabels.EL_NIDO, disabled: true }
  ];

  productOptions: OptionInterface[] = [
    { label: XpassProductLabels.STRESS_FREE, value: XpassProductLabels.STRESS_FREE, disabled: false },
    { label: XpassProductLabels.GOVT_FEE, value: XpassProductLabels.GOVT_FEE, disabled: true },
    { label: XpassProductLabels.CONCIERGE, value: XpassProductLabels.CONCIERGE, disabled: true }
  ];

  // Filter values
  filterTransferOrderId: string = '';
  filterCustomerName: string = '';
  filterLocation: string | null = null;
  filterProduct: string | null = null;
  filterPickupLocation: string = '';
  filterDropOffLocation: string = '';
  filterStatus: string | null = null;
  filterIpassValid: string | null = null;
  filterScheduledDate: Date | null = null;
  filterLastUpdatedDate: Date | null = null;
  filterIsModified: string | null = null;
  filterPassengerCount: string = '';
  filterStubCount: string = '';
  filterTripType: string | null = null;

  // Date range filter
  public filter: {
    startDate: Date | null;
    endDate: Date | null;
  } = {
    startDate: null,
    endDate: null
  };

  transactionId!: number;
  showXpassBookingDetailSideBar = false;
  selectedTransaction?: XpassOrdersTableRow;

  pisoPayWalletBalance: string = '---';
  isLoadingBalance: boolean = false;
  totalStubCount: number = 0;

  @ViewChild('xpassDataTable') xpassDataTable?: Table;

  private orderService = inject(OrderService);
  private commonTypeService = inject(CommonTypeService);

  showAddNotes: boolean = false;
  showReassignDriver: boolean = false;
  transferNote: string = '';
  noteHeader: string = '';

  public showExportSidebar: boolean = false;
  public exportLoading: boolean = false;
  public exportFileTypes = ExportFileTypeModel.ExportFileTypes;
  public exportData: {
    fileType: ExportFileTypeEnum | null;
  } = { fileType: null };

  private lastLazyLoadEvent: TableLazyLoadEvent | null = null;

  constructor(private messageService: MessageService, private xpassService: OrderService) {}

  ngOnInit(): void {
    this.getWalletBalance();
  }
    private getWalletBalance(): void {
    this.isLoadingBalance = true;
    this.orderService
      .getWalletBalance()
      .then((response) => {
        if (response.Success) {
          this.pisoPayWalletBalance = '₱ ' + response.Balance.toLocaleString('en-PH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        } else {
          this.pisoPayWalletBalance = 'Wallet Balance Unavailable';
        }
      })
      .catch((error) => {
        console.error('Failed to get wallet balance:', error);
        this.pisoPayWalletBalance = 'Wallet Balance Unavailable';
      })
      .finally(() => {
        this.isLoadingBalance = false;
      });
  }

  loadOrders(event: TableLazyLoadEvent): void {
    this.loading = true;
    this.lastLazyLoadEvent = event;

    const request = this.buildRequest(event);

    this.orderService.getTransferOrders(request)
      .then(({ TransferOrders, TotalCount }) => {
        this.orders = this.formatIntoTableValues(TransferOrders);
        this.totalRecords = TotalCount;
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load orders'
        });
      });
  }

  private buildRequest(event: TableLazyLoadEvent): GetTransferOrdersRequest {
    const request: GetTransferOrdersRequest = {
      Skip: event.first || 0,
      Take: event.rows || this.rows,
      SortField: (event.sortField as string) || '',
      SortOrder: event.sortOrder || 0,
      TimeZoneOffSet: Utils.getTimezoneOffset()
    };

    // Apply filters
    if (this.filterTransferOrderId) {
      request.BookingIdSearch = this.filterTransferOrderId;
    }
    if (this.filterCustomerName) {
      request.CustomerName = this.filterCustomerName;
    }
    if (this.filterLocation) {
      request.Location = this.commonTypeService.formatLabelIntoLocation(this.filterLocation);
    }
    if (this.filterProduct) {
      request.Product = this.commonTypeService.formatLabelIntoProduct(this.filterProduct);
    }
    if (this.filterPickupLocation) {
      request.PickupLocation = this.filterPickupLocation;
    }
    if (this.filterDropOffLocation) {
      request.DropOffLocation = this.filterDropOffLocation;
    }
    if (this.filterStatus) {
      request.Status = this.commonTypeService.formatLabelIntoStatus(this.filterStatus);
    }
    if (this.filterIpassValid) {
      request.IpassValid = this.filterIpassValid === IpassValidOptionLabels.YES;
    }
    if (this.filter.startDate) {
      request.ScheduledDateFrom = this.filter.startDate.toISOString();
    }
    if (this.filter.endDate) {
      request.ScheduledDateTo = this.filter.endDate.toISOString();
    }
    if (this.filterScheduledDate) {
      request.ScheduledDateFrom = this.filterScheduledDate.toISOString();
      const endOfDay = new Date(this.filterScheduledDate);
      endOfDay.setHours(23, 59, 59, 999);
      request.ScheduledDateTo = endOfDay.toISOString();
    }
    if (this.filterLastUpdatedDate) {
      request.LastUpdatedDate = this.filterLastUpdatedDate.toISOString();
    }
    if (this.filterPassengerCount) {
      request.PassengerCount = parseInt(this.filterPassengerCount, 10) || undefined;
    }
    if (this.filterStubCount) {
      request.StubCount = parseInt(this.filterStubCount, 10) || undefined;
    }
    if (this.filterTripType) {
      request.TripType = this.commonTypeService.formatLabelIntoTripType(this.filterTripType);
    }

    return request;
  }

  private formatIntoTableValues(orders: XpassOrder[]): XpassOrdersTableRow[] {
    if (!orders || !orders.length) {
      return [];
    }

    return orders.map((order: XpassOrder) => {
      const { TransferOrderId, CustomerName, IsModified, IpassValid, PassengerCount, StubCount } = order;
      const { PickupLocation, DropOffLocation } = order;
      const { SubDropOffLocation, SubPickupLocation } = order;
      const { ScheduledDate, LastUpdatedDate } = order;
      const { Location, Product, Status, TripType } = order;

      return {
        TransferOrderId,
        CustomerName,
        PickupLocation,
        DropOffLocation,
        PassengerCount,
        ScheduledDate: new Date(
          moment(Utils.utcToLocalDate(ScheduledDate)).format(DATE_FORMAT)
        ),
        LastUpdatedDate: new Date(
          moment(Utils.utcToLocalDate(LastUpdatedDate)).format(DATE_FORMAT)
        ),
        SubDropOffLocation,
        SubPickupLocation,
        Location: this.commonTypeService.formatLocationIntoLabel(Location),
        Product: this.commonTypeService.formatProductIntoLabel(Product),
        TripType: this.commonTypeService.formatTripTypeIntoLabel(TripType),
        Status: this.commonTypeService.formatStatusIntoLabel(Status),
        IpassValid: IpassValid
          ? IpassValidOptionLabels.YES
          : IpassValidOptionLabels.NO,
        IsModified: IsModified
          ? IsModifiedOptionLabels.YES
          : IsModifiedOptionLabels.NO,
        StubCount
      };
    });
  }

  applyFilter(): void {
    if (this.xpassDataTable) {
      this.xpassDataTable.first = 0;
      this.loadOrders({
        first: 0,
        rows: this.rows,
        sortField: this.lastLazyLoadEvent?.sortField || '',
        sortOrder: this.lastLazyLoadEvent?.sortOrder || 0
      });
    }
  }

  clearFilter(field: string): void {
    switch (field) {
      case 'TransferOrderId': this.filterTransferOrderId = ''; break;
      case 'CustomerName': this.filterCustomerName = ''; break;
      case 'Location': this.filterLocation = null; break;
      case 'Product': this.filterProduct = null; break;
      case 'PickupLocation': this.filterPickupLocation = ''; break;
      case 'DropOffLocation': this.filterDropOffLocation = ''; break;
      case 'Status': this.filterStatus = null; break;
      case 'IpassValid': this.filterIpassValid = null; break;
      case 'ScheduledDate': this.filterScheduledDate = null; break;
      case 'LastUpdatedDate': this.filterLastUpdatedDate = null; break;
      case 'IsModified': this.filterIsModified = null; break;
      case 'PassengerCount': this.filterPassengerCount = ''; break;
      case 'StubCount': this.filterStubCount = ''; break;
      case 'TripType': this.filterTripType = null; break;
    }
    this.applyFilter();
  }

  onChangeHeaderFilterInput(event: Event, field: string): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;

    switch (field) {
      case 'StubCount':
        this.filterStubCount = value;
        break;
    }
    this.applyFilter();
  }

  public filterTableByDateRange(): void {
    this.applyFilter();
  }

  openPartnerFinanceTransactionDetailSideBar(transaction: XpassOrdersTableRow) {
    this.selectedTransaction = transaction;
    this.showXpassBookingDetailSideBar = true;
  }

  public openExportSidebar(): void {
    this.showExportSidebar = true;
  }

  public changedVisibleExportSidebar(visible: boolean): void {
    this.showExportSidebar = visible;
  }

  public export(): void {
    const fileType = this.exportData.fileType;
    if (!fileType) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please select file type'
      });
      return;
    }

    if (this.totalRecords === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No orders match your filter criteria. Please adjust your filters.'
      });
      return;
    }

    const filters = this.buildRequest({
      first: 0,
      rows: 0,
      sortField: this.lastLazyLoadEvent?.sortField || '',
      sortOrder: this.lastLazyLoadEvent?.sortOrder || 0
    });
    const startDate = this.filter.startDate ? moment(this.filter.startDate).format('MM/DD/YYYY') : '';
    const endDate = this.filter.endDate ? moment(this.filter.endDate).format('MM/DD/YYYY') : '';

    this.exportLoading = true;

    this.xpassService.exportTransferOrders(fileType, filters, startDate, endDate, this.messageService)
      .then((data) => {
        const fileExtension = ExportFileExtension[ExportFileTypeEnum[fileType] as keyof typeof ExportFileExtension];
        const fileName = 'Xpass_Orders_' + moment(new Date()).format('DD_MMM_YYYY_hh_mm_ss_A') + fileExtension;
        Utils.saveFileResponse(data, fileName);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Export completed successfully'
        });
        this.exportLoading = false;
        this.showExportSidebar = false;
      })
      .catch(err => {
        this.exportLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Export failed'
        });
        throw err;
      });
  }

}
